/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useCallback } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import HomeIcon from '~components/_common/HomeIcon/HomeIcon';
import { navigationItemsMobile } from '~src/data/navigationItems';
import { useModules } from '~src/hooks/api-hooks/useModules';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { useProjectId } from '~src/hooks/useProjectId';
import {
  getPrimarySVFRender,
  getPrimarySVFRenderLocation,
} from '~src/services/api';
import { appLog } from '~src/utils/log';
import { message } from 'antd';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { useIntl } from 'react-intl';

interface HomeMobileProps {
  test?: boolean;
}

export const HomeMobile: React.FC<HomeMobileProps> = ({}) => {
  const styles = useStyles(styleFn);
  const modulesQuery = useModules();
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const projectId = useProjectId();
  const archiveSettings = useArchiveSettings();
  const { setFileToView } = useFileViewerContext();
  const intl = useIntl();

  const goTo = (path: string) => {
    const urlPath = path.length > 1 ? path : '';
    history.push(routeMatch.url + urlPath);
  };

  const openPrimarySVFfile = useCallback(async () => {
    if (!projectId) {
      appLog('[HomeMobile] Missing projectId, cannot open primary SVF');
      return;
    }

    try {
      const meta = await getPrimarySVFRender(Number(projectId)); // Now returns null on 404

      if (!meta) {
        // Check for the null result from the API function
        message.error(
          intl.formatMessage({ id: 'primary-svf-file-preview-error-null' })
        );
        return;
      }

      const loc = await getPrimarySVFRenderLocation(Number(projectId));

      if (!meta?.file_guid || !loc?.urn || !loc?.access_token) {
        message.error(
          intl.formatMessage({ id: 'primary-svf-file-preview-error-null' })
        );
        return;
      }

      setFileToView({
        id: meta.file_guid || 0,
        title: meta.versioned_file_title,
        folder_id: meta.versioned_file_folder_id,
        serial_number: meta.file_serial_number,
        autodesk_object_id: meta.autodesk_object_id,
        urn: loc.urn,
        access_token: loc.access_token,
      } as any);
    } catch (err) {
      appLog('[HomeMobile] openPrimarySVFfile error', err);
    }
  }, [projectId, setFileToView]);

  return (
    <div css={styles.container}>
      {navigationItemsMobile
        .filter((item) => item.section === 'project-data')
        .map((item) => {
          if (item.key === 'primary-svf-viewer') {
            const hasPrimarySVF = !!archiveSettings?.data?.resArchiveSettings
              ?.primary_svf_render_id;

            return (
              <HomeIcon
                key={item.key}
                name={item.nameTerm}
                iconStyle={item.icon[0]}
                icon={item.icon[1]}
                customStyle={[
                  styles.icons,
                  hasPrimarySVF ? styles.grayBlue : styles.red,
                ]}
                click={openPrimarySVFfile}
                containerStyle={{
                  margin: 0,
                  maxWidth: '100%',
                  width: '100%',
                }}
              />
            );
          }

          // default for every other item in project-data
          return (
            <HomeIcon
              key={item.key}
              name={item.nameTerm}
              iconStyle={item.icon[0]}
              icon={item.icon[1]}
              customStyle={[styles.icons, styles[item.color]]}
              click={() => goTo(item.path)}
              containerStyle={{
                margin: 0,
                maxWidth: '100%',
                width: '100%',
              }}
            />
          );
        })}
      {navigationItemsMobile
        .filter(
          (v) =>
            v.section === 'special' &&
            modulesQuery.data &&
            v.moduleName &&
            modulesQuery.data[v.moduleName]
        )
        .map((item) => (
          <HomeIcon
            key={item.key}
            name={item.nameTerm}
            iconStyle={item.icon[0]}
            icon={item.icon[1]}
            customIcon={item.customIcon}
            customStyle={[styles.icons, styles[item.color]]}
            click={() => goTo(item.path)}
            containerStyle={{
              margin: 0,
              maxWidth: '100%',
              width: '100%',
              paddingLeft: 26,
            }}
            textStyle={{ paddingLeft: 5 }}
          />
        ))}
    </div>
  );
};

export const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  blue: {
    color: theme.colors.primary,
  },
  grayBlue: {
    color: '#184a9a',
  },
  red: {
    color: theme.colors.red,
  },
  green: {
    color: theme.colors.green,
  },
  darkGray: {
    color: theme.colors.darkGray,
  },
  yellow: {
    color: theme.colors.yellow,
  },
  icons: {
    color: theme.colors.secondary,
    fontSize: '40px',
    margin: '0 6px',
  },
});
